import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { api } from '../lib/api.js';

const LOGIN_KEYS = new Set([
  'Enter',
  'Tab',
  'Backspace',
  'Escape',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

const SALESFORCE_HINT =
  'Type your username and press Enter. The password field is the next page, then the MFA code.';
export const GOOGLE_VOICE_HINT =
  'Sign in with the Google account that owns the Google Voice number: email, Enter, password, Enter, then any 2-step check.';

export function SalesforceLoginFrame({
  fill = false,
  hint = SALESFORCE_HINT,
}: {
  fill?: boolean;
  hint?: string;
}): ReactNode {
  const imageRef = useRef<HTMLImageElement>(null);
  const viewport = useRef({ width: 1100, height: 720 });
  const [frameUrl, setFrameUrl] = useState<string>();
  const [pageUrl, setPageUrl] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    let current: string | undefined;

    const tick = async () => {
      try {
        const response = await api.salesforceConnectScreen();
        if (cancelled || !response.ok || response.status === 204) return;
        const width = Number(response.headers.get('X-Viewport-Width'));
        const height = Number(response.headers.get('X-Viewport-Height'));
        if (width > 0 && height > 0) viewport.current = { width, height };
        const reported = response.headers.get('X-Page-Url');
        if (reported) setPageUrl(reported);
        const next = URL.createObjectURL(await response.blob());
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        if (current) URL.revokeObjectURL(current);
        current = next;
        setFrameUrl(next);
      } catch {
        // Keep the last frame while Salesforce is navigating.
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), 120);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      if (current) URL.revokeObjectURL(current);
    };
  }, []);

  function point(event: MouseEvent<HTMLImageElement>): { x: number; y: number } | undefined {
    const image = imageRef.current;
    if (!image) return undefined;
    const rect = image.getBoundingClientRect();
    return containedImagePoint(event.clientX, event.clientY, rect, viewport.current);
  }

  function onKeyDown(event: KeyboardEvent<HTMLImageElement>): void {
    event.preventDefault();
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const send =
      event.key.length === 1
        ? api.salesforceConnectInput({ action: 'type', text: event.key })
        : LOGIN_KEYS.has(event.key)
          ? api.salesforceConnectInput({ action: 'press', key: event.key })
          : undefined;
    void send?.catch(() => undefined);
  }

  return (
    <div
      className="stack stack--tight"
      style={
        fill
          ? { minHeight: '100vh', background: '#101216', display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      <span className="muted" style={{ fontSize: 12.5, color: fill ? '#d5d8e0' : undefined, padding: fill ? '12px 16px 0' : 0 }}>
        {hint}
        {pageUrl ? ` ${pageUrl}` : ''}
      </span>
      {frameUrl ? (
        <img
          ref={imageRef}
          src={frameUrl}
          alt="Sign-in window"
          tabIndex={0}
          autoFocus
          onMouseDown={(event) => {
            event.currentTarget.focus();
            const at = point(event);
            if (!at) return;
            void api.salesforceConnectInput({ action: 'click', x: at.x, y: at.y }).catch(() => undefined);
          }}
          onKeyDown={onKeyDown}
          style={{
            width: '100%',
            maxWidth: fill ? 'none' : 1100,
            height: fill ? 'auto' : undefined,
            flex: fill ? 1 : undefined,
            objectFit: 'contain',
            background: '#101216',
            border: fill ? 'none' : '1px solid var(--border-strong)',
            borderRadius: fill ? 0 : 'var(--radius-md)',
            cursor: 'default',
            outline: 'none',
          }}
        />
      ) : (
        <p className="muted" style={{ fontSize: 12.5, color: fill ? '#d5d8e0' : undefined, padding: fill ? 24 : 0 }}>
          Opening the sign-in page…
        </p>
      )}
    </div>
  );
}

/** Maps a click on the letterboxed image back to the browser pixel it shows. */
export function containedImagePoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  viewport: { width: number; height: number },
): { x: number; y: number } | undefined {
  if (rect.width === 0 || rect.height === 0 || viewport.width === 0 || viewport.height === 0) return undefined;
  const scale = Math.min(rect.width / viewport.width, rect.height / viewport.height);
  const renderW = viewport.width * scale;
  const renderH = viewport.height * scale;
  const localX = clientX - rect.left - (rect.width - renderW) / 2;
  const localY = clientY - rect.top - (rect.height - renderH) / 2;
  if (localX < 0 || localY < 0 || localX > renderW || localY > renderH) return undefined;
  return { x: (localX / renderW) * viewport.width, y: (localY / renderH) * viewport.height };
}

/** The sign-in window. The server cannot open a window on your computer, so this is that window. */
export function SalesforceLoginWindow(): ReactNode {
  const [done, setDone] = useState<string>();
  const [target, setTarget] = useState<'salesforce' | 'google-voice'>();

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await api.salesforceConnectStatus();
        if (!cancelled && status.target) setTarget(status.target);
        if (cancelled || status.status === 'waiting') return;
        setDone(
          status.message ??
            (status.status === 'saved' ? 'Signed in. You can close this window.' : 'Sign-in ended.'),
        );
        if (status.status === 'saved') window.setTimeout(() => window.close(), 1200);
      } catch {
        // The settings page is also watching this sign-in.
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (done) {
    return (
      <p style={{ padding: 24, color: '#14161a' }}>
        {done}
      </p>
    );
  }

  return <SalesforceLoginFrame fill {...(target === 'google-voice' ? { hint: GOOGLE_VOICE_HINT } : {})} />;
}
